import ObjectId from 'bson-objectid';
import {cloneDeep} from 'lodash';
import React, {useMemo, useState} from 'react';
import {Button, Dropdown, Form, Icon, Input, Table} from 'semantic-ui-react';
import {User} from '../models/User';

interface ChecklistProps {
  data: any;
  setTasks: any;
  loggedInUser: User;
}

const assignedToOptions = [
  {key: 'all', value: 'all', text: 'All'},
  {key: 'onsite', value: 'onsite', text: 'Onsite Users'},
  {key: 'admin', value: 'admin', text: 'Admin Users'},
];

const ChecklistTemplate: React.FunctionComponent<ChecklistProps> = (
  props: ChecklistProps,
) => {
  const [task, setTask] = useState<string>();
  const [assignedTo, setAssignedTo] = useState<string>('all');
  const [group, setGroup] = useState<string>('');
  const [customGroups, setCustomGroups] = useState<string[]>([]);

  const groupOptions = useMemo(() => {
    const tasks = props.data?.['checklist.tasks'] || [];
    const taskGroups = tasks.map((t: any) => t.group).filter(Boolean) as string[];
    const allGroups = [...new Set([...taskGroups, ...customGroups])];
    return allGroups.map((g) => ({key: g, value: g, text: g}));
  }, [props.data, customGroups]);

  const deleteTask = (id) => {
    const cloned = props.data['checklist.tasks'].filter((i) => i._id !== id);
    props.setTasks(cloned);
  };

  return (
    <div className={'ChecklistTemplate'}>
      <h1>Add Task</h1>

      <div className={'AddTask'}>
        <Form.Field>
          <Input
            style={{marginBottom: 10}}
            placeholder={`Enter a task`}
            value={task}
            onChange={(e, data) => {
              setTask(data.value);
            }}
          />
        </Form.Field>
        <Form.Group widths="equal" style={{marginBottom: 10}}>
          <Form.Field>
            <label>Assigned To</label>
            <Dropdown
              selection
              options={assignedToOptions}
              value={assignedTo}
              onChange={(_, data) => setAssignedTo(data.value as string)}
            />
          </Form.Field>
          <Form.Field>
            <label>Group</label>
            <Dropdown
              selection
              search
              allowAdditions
              placeholder="Select or type a group"
              options={groupOptions}
              value={group}
              onAddItem={(_, data) => {
                const val = data.value as string;
                setCustomGroups((prev) => [...new Set([...prev, val])]);
                setGroup(val);
              }}
              onChange={(_, data) => setGroup(data.value as string)}
              noResultsMessage="Type to add a new group"
            />
          </Form.Field>
        </Form.Group>
        <Button
          disabled={!task?.length}
          primary
          onClick={() => {
            const taskObj: any = {
              _id: new ObjectId().toHexString(),
              created: new Date(),
              createdBy: props.loggedInUser._id,
              text: task,
              status: 'not-completed',
              assignedTo,
            };
            if (group) taskObj.group = group;

            const cloned = cloneDeep(props.data['checklist.tasks'] || []);
            cloned.push(taskObj);

            props.setTasks(cloned);

            setTask('');
          }}
        >
          Add Task
        </Button>
      </div>

      <h1>Tasks</h1>

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell textAlign={'center'}>Task Name</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Assigned To</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Group</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {(!props.data ||
            !props.data['checklist.tasks'] ||
            !props.data['checklist.tasks'].length) && (
            <Table.Row>
              <Table.Cell colSpan={4} textAlign={'center'}>
                <p>No tasks found</p>
              </Table.Cell>
            </Table.Row>
          )}

          {props.data &&
            props.data['checklist.tasks'] &&
            props.data['checklist.tasks'].map((i) => {
              return (
                <Table.Row key={`checklist-task-${i.text}-${i._id}`}>
                  <Table.Cell width={6} textAlign={'center'}>
                    <p>{i.text}</p>
                  </Table.Cell>
                  <Table.Cell width={3} textAlign={'center'}>
                    <p>
                      {i.assignedTo === 'onsite'
                        ? 'Onsite Users'
                        : i.assignedTo === 'admin'
                        ? 'Admin Users'
                        : 'All'}
                    </p>
                  </Table.Cell>
                  <Table.Cell width={3} textAlign={'center'}>
                    <p>{i.group || '-'}</p>
                  </Table.Cell>
                  <Table.Cell width={1} textAlign={'center'}>
                    <Button
                      className={'redButton'}
                      icon={true}
                      labelPosition={'left'}
                      onClick={() => deleteTask(i._id)}
                    >
                      <Icon name="delete" />
                      Delete
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default ChecklistTemplate;
